import React, { useState, useEffect, useRef } from 'react';
import {
  Box, Button, Typography, Card, CardContent, Alert, CircularProgress,
  Chip, IconButton, TextField, Switch, FormControlLabel, Divider,
  Table, TableHead, TableBody, TableRow, TableCell, TableContainer,
  Paper, Tooltip, Snackbar, Stack, useMediaQuery,
} from '@mui/material';
import {
  CloudUpload as UploadIcon,
  Send as SendIcon,
  NotificationsActive as ReminderIcon,
  CheckCircle as CheckIcon,
  Cancel as CancelIcon,
  HelpOutline as HelpIcon,
  OpenInNew as OpenIcon,
  Delete as DeleteIcon,
  MailOutline as MailIcon,
  Palette as PaletteIcon,
} from '@mui/icons-material';
import { useTheme, alpha } from '@mui/material/styles';
import { format } from 'date-fns';
import { hu } from 'date-fns/locale';
import weddingEventService from '../../services/weddingEventService';
import SendInvitationsDialog from './SendInvitationsDialog';
import InvitationPreviewDialog from './InvitationPreviewDialog';

const RSVP_COLOR = { NO_RESPONSE: 'default', ATTENDING: 'success', NOT_ATTENDING: 'error', MAYBE: 'warning' };
const RSVP_LABEL = { NO_RESPONSE: 'Nincs válasz', ATTENDING: 'Részt vesz', NOT_ATTENDING: 'Nem jön', MAYBE: 'Talán' };
const ALLOWED_MIME = 'image/jpeg,image/jpg,image/png,image/svg+xml,image/webp,image/gif,application/pdf';

export default function InvitationsTab({ event }) {
  // Bemutatóban a kitalált vendégeknek nem küldünk levelet (a backend is elutasítja)
  const isDemo = !!event?.isDemo;
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const fileInputRef = useRef(null);

  const [invitation, setInvitation] = useState(null);
  const [guests, setGuests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [sendDialogOpen, setSendDialogOpen] = useState(false);
  const [previewDialogOpen, setPreviewDialogOpen] = useState(false);
  const [reminderLoading, setReminderLoading] = useState(false);
  const [snack, setSnack] = useState({ open: false, msg: '', severity: 'success' });

  const [settings, setSettings] = useState({
    customMessage: '',
    rsvpDeadline: '',
    allowPlusOne: true,
  });

  useEffect(() => { fetchData(); }, [event.id]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [invRes, guestRes] = await Promise.all([
        weddingEventService.getInvitation(event.id),
        weddingEventService.getGuests(event.id),
      ]);
      if (invRes.success && invRes.data) {
        setInvitation(invRes.data);
        setSettings({
          customMessage: invRes.data.customMessage || '',
          rsvpDeadline: invRes.data.rsvpDeadline
            ? invRes.data.rsvpDeadline.split('T')[0]
            : '',
          allowPlusOne: invRes.data.allowPlusOne ?? true,
        });
      }
      if (guestRes.success) setGuests(guestRes.data || []);
    } catch (err) {
      showSnack('Hiba a betöltés során', 'error');
    } finally {
      setLoading(false);
    }
  };

  const showSnack = (msg, severity = 'success') =>
    setSnack({ open: true, msg, severity });

  const handleFile = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const res = await weddingEventService.uploadInvitationImage(event.id, file);
      if (res.success) {
        setInvitation(res.data);
        showSnack('Meghívó feltöltve!');
      } else {
        showSnack(res.error || 'Feltöltési hiba', 'error');
      }
    } catch (err) {
      showSnack(err.message || 'Feltöltési hiba', 'error');
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files[0];
    if (file) handleFile(file);
  };

  const handleSaveSettings = async () => {
    setSaving(true);
    try {
      const res = await weddingEventService.saveInvitationSettings(event.id, {
        customMessage: settings.customMessage || null,
        rsvpDeadline: settings.rsvpDeadline || null,
        allowPlusOne: settings.allowPlusOne,
      });
      if (res.success) {
        setInvitation(res.data);
        showSnack('Beállítások mentve!');
      } else {
        showSnack(res.error || 'Mentési hiba', 'error');
      }
    } catch (err) {
      showSnack(err.message || 'Mentési hiba', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleSendReminders = async () => {
    setReminderLoading(true);
    try {
      const res = await weddingEventService.sendReminders(event.id);
      if (res.success) {
        const sentCount = res.data.filter(r => r.status === 'sent').length;
        showSnack(`${sentCount} emlékeztető kiküldve!`);
        await fetchData();
      } else {
        showSnack(res.error || 'Küldési hiba', 'error');
      }
    } catch (err) {
      showSnack(err.message || 'Küldési hiba', 'error');
    } finally {
      setReminderLoading(false);
    }
  };

  const handleSendSingle = async (guestId) => {
    try {
      const res = await weddingEventService.sendSingleInvitation(event.id, guestId);
      if (res.success) {
        showSnack('Meghívó kiküldve!');
        await fetchData();
      } else {
        showSnack(res.error || 'Küldési hiba', 'error');
      }
    } catch (err) {
      showSnack(err.message || 'Küldési hiba', 'error');
    }
  };

  const noResponseCount = guests.filter(g => g.rsvpStatus === 'NO_RESPONSE' && g.invitationSentAt).length;

  // --- Mobil kártyalista: ugyanaz a mintát követi, mint a Vendégek fülön ---
  const InvitationGuestMobileList = () => (
    <Stack spacing={1.5}>
      {guests.map(guest => (
        <Card key={guest.id} sx={{ borderRadius: '12px', border: `1px solid ${theme.palette.divider}`, boxShadow: '0 1px 4px rgba(0,0,0,0.06)' }}>
          <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 1, mb: 0.75 }}>
              <Box sx={{ minWidth: 0 }}>
                <Typography variant="subtitle2" fontWeight={700} noWrap>
                  {guest.lastName} {guest.firstName}
                </Typography>
                <Typography variant="caption" color={guest.email ? 'text.secondary' : 'text.disabled'} noWrap sx={{ display: 'block' }}>
                  {guest.email || 'Nincs email cím'}
                </Typography>
              </Box>
              <Tooltip title={!guest.email ? 'Nincs email cím' : guest.invitationSentAt ? 'Újraküldés' : 'Küldés'}>
                <span>
                  <IconButton
                    size="small"
                    color="primary"
                    disabled={isDemo || !guest.email || !invitation?.imageUrl}
                    onClick={() => handleSendSingle(guest.id)}
                    sx={{ flexShrink: 0 }}
                  >
                    <SendIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
              <Chip
                label={RSVP_LABEL[guest.rsvpStatus] || guest.rsvpStatus}
                color={RSVP_COLOR[guest.rsvpStatus] || 'default'}
                size="small"
                variant={guest.rsvpStatus === 'NO_RESPONSE' ? 'outlined' : 'filled'}
              />
              {guest.invitationSentAt ? (
                <Tooltip title={format(new Date(guest.invitationSentAt), 'yyyy. MM. dd. HH:mm', { locale: hu })}>
                  <Chip icon={<CheckIcon />} label="Kiküldve" size="small" color="success" variant="outlined" />
                </Tooltip>
              ) : (
                <Chip label="Nem küldték ki" size="small" variant="outlined" color="default" />
              )}
            </Box>

            {guest.plusOneName && (
              <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 0.5 }}>
                +1: {guest.plusOneName}
              </Typography>
            )}
            {guest.guestNotes && (
              <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 0.5 }}>
                Megjegyzés: {guest.guestNotes}
              </Typography>
            )}
          </CardContent>
        </Card>
      ))}
    </Stack>
  );

  const cardSx = {
    borderRadius: '16px',
    border: `1px solid ${theme.palette.divider}`,
    background: theme.palette.background.paper,
    boxShadow: `0 1px 3px ${alpha(theme.palette.primary.dark, 0.06)}`,
    mb: 3,
  };

  const sectionHeader = (title, icon) => (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
      {React.cloneElement(icon, { sx: { color: 'secondary.main', fontSize: 20 } })}
      <Typography variant="h6" sx={{ fontWeight: 700, color: 'primary.dark', fontSize: '1rem' }}>
        {title}
      </Typography>
      <Box sx={{ flex: 1, height: 1, bgcolor: alpha(theme.palette.secondary.main, 0.25), ml: 1 }} />
    </Box>
  );

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress color="primary" />
      </Box>
    );
  }

  return (
    <Box>
      {/* Meghívó feltöltése */}
      <Card sx={cardSx}>
        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
          {sectionHeader('Meghívó képe / fájlja', <UploadIcon />)}

          <Box
            onDrop={handleDrop}
            onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
            onDragLeave={() => setDragOver(false)}
            onClick={() => !uploading && fileInputRef.current?.click()}
            sx={{
              border: `2px dashed ${dragOver ? theme.palette.secondary.main : alpha(theme.palette.primary.main, 0.3)}`,
              borderRadius: '12px',
              p: { xs: 3, sm: 4 },
              textAlign: 'center',
              cursor: uploading ? 'not-allowed' : 'pointer',
              bgcolor: dragOver ? alpha(theme.palette.secondary.light, 0.15) : alpha(theme.palette.background.default, 0.5),
              transition: 'all 0.2s',
              '&:hover': { bgcolor: alpha(theme.palette.secondary.light, 0.12), borderColor: 'secondary.main' },
              mb: 2,
            }}
          >
            <input
              ref={fileInputRef}
              type="file"
              accept={ALLOWED_MIME}
              hidden
              onChange={e => handleFile(e.target.files[0])}
            />
            {uploading ? (
              <CircularProgress size={32} color="secondary" />
            ) : (
              <>
                <UploadIcon sx={{ fontSize: 40, color: 'secondary.main', mb: 1 }} />
                <Typography variant="body1" sx={{ fontWeight: 600, color: 'primary.dark' }}>
                  {isMobile ? 'Koppints ide a meghívó kiválasztásához' : 'Húzd ide a fájlt, vagy kattints a tallózáshoz'}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  JPG, PNG, SVG, WebP, GIF, PDF • max. 20 MB
                </Typography>
              </>
            )}
          </Box>

          {/* Preview */}
          {invitation?.imageUrl && (
            <Box sx={{ mt: 1 }}>
              {invitation.imageType === 'pdf' ? (
                <Button
                  variant="outlined"
                  startIcon={<OpenIcon />}
                  href={invitation.imageUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  size="small"
                >
                  PDF meghívó megtekintése
                </Button>
              ) : (
                <Box sx={{ position: 'relative', display: 'inline-block' }}>
                  <Box
                    component="img"
                    src={invitation.imageUrl}
                    alt="Meghívó előnézet"
                    sx={{
                      maxWidth: '100%',
                      maxHeight: 360,
                      borderRadius: '8px',
                      boxShadow: `0 4px 16px ${alpha(theme.palette.primary.dark, 0.15)}`,
                      display: 'block',
                    }}
                  />
                  <Tooltip title="Csere / újrafeltöltés">
                    <IconButton
                      size="small"
                      onClick={(e) => { e.stopPropagation(); fileInputRef.current?.click(); }}
                      sx={{
                        position: 'absolute', top: 8, right: 8,
                        bgcolor: alpha('#fff', 0.9),
                        '&:hover': { bgcolor: '#fff' },
                      }}
                    >
                      <UploadIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>
              )}
              <Box sx={{ mt: 2 }}>
                <Button
                  variant="outlined"
                  startIcon={<PaletteIcon />}
                  onClick={() => setPreviewDialogOpen(true)}
                >
                  Előnézet és megjelenés szerkesztése
                </Button>
              </Box>
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Beállítások */}
      <Card sx={cardSx}>
        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
          {sectionHeader('Beállítások', <MailIcon />)}

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              label="RSVP határidő"
              type="date"
              value={settings.rsvpDeadline}
              onChange={e => setSettings(s => ({ ...s, rsvpDeadline: e.target.value }))}
              InputLabelProps={{ shrink: true }}
              size="small"
              sx={{ maxWidth: 240 }}
            />
            <TextField
              label="Személyes üzenet"
              multiline
              rows={3}
              value={settings.customMessage}
              onChange={e => setSettings(s => ({ ...s, customMessage: e.target.value }))}
              placeholder="Pl: Szeretettel várunk titeket a nagy napunkon..."
              fullWidth
            />
            <FormControlLabel
              control={
                <Switch
                  checked={settings.allowPlusOne}
                  onChange={e => setSettings(s => ({ ...s, allowPlusOne: e.target.checked }))}
                />
              }
              label="Kísérő hozható"
            />
            <Box>
              <Button
                variant="contained"
                onClick={handleSaveSettings}
                disabled={saving}
                startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <CheckIcon />}
              >
                Mentés
              </Button>
            </Box>
          </Box>
        </CardContent>
      </Card>

      {/* Küldés gombok */}
      <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
        {/* Letiltott gombnál mondjuk meg, miért nem kattintható */}
        <Tooltip title={isDemo ? 'A bemutató esküvőből nem küldhető meghívó' : !invitation?.imageUrl ? 'Előbb tölts fel egy meghívót' : ''}>
          <span>
            <Button
              variant="contained"
              color="primary"
              startIcon={<SendIcon />}
              onClick={() => setSendDialogOpen(true)}
              disabled={isDemo || !invitation?.imageUrl}
            >
              Meghívók tömeges küldése
            </Button>
          </span>
        </Tooltip>
        <Tooltip title={noResponseCount === 0 ? 'Nincs visszajelzés nélküli vendég, aki már kapott meghívót' : ''}>
          <span>
            <Button
              variant="outlined"
              color="warning"
              startIcon={reminderLoading ? <CircularProgress size={16} /> : <ReminderIcon />}
              onClick={handleSendReminders}
              disabled={isDemo || reminderLoading || noResponseCount === 0}
            >
              Emlékeztető küldése ({noResponseCount} fő)
            </Button>
          </span>
        </Tooltip>
      </Box>

      {!invitation?.imageUrl && (
        <Alert severity="info" sx={{ mb: 3 }}>
          Tölts fel egy meghívót, utána tudod kiküldeni a vendégeknek.
        </Alert>
      )}

      {/* Vendéglista állapota */}
      <Card sx={cardSx}>
        <CardContent sx={{ p: { xs: 1, sm: 2 } }}>
          {sectionHeader('Vendéglista állapota', <MailIcon />)}
          {guests.length === 0 ? (
            <Alert severity="info">Még nincsenek vendégek. Adjon hozzá vendégeket a Vendégek fülön.</Alert>
          ) : isMobile ? (
            <InvitationGuestMobileList />
          ) : (
            <TableContainer component={Paper} elevation={0} sx={{ border: `1px solid ${theme.palette.divider}`, borderRadius: '8px' }}>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ bgcolor: alpha(theme.palette.secondary.light, 0.18) }}>
                    <TableCell sx={{ fontWeight: 700 }}>Vendég neve</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Email</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>RSVP státusz</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Meghívó kiküldve</TableCell>
                    <TableCell sx={{ fontWeight: 700, textAlign: 'right' }}>Küldés</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {guests.map(guest => (
                    <TableRow key={guest.id} hover>
                      <TableCell>
                        <Typography variant="body2" fontWeight={600}>
                          {guest.lastName} {guest.firstName}
                        </Typography>
                        {guest.guestNotes && (
                          <Typography variant="caption" color="text.secondary" display="block">
                            Megjegyzés: {guest.guestNotes}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color={guest.email ? 'text.primary' : 'text.disabled'}>
                          {guest.email || '—'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={RSVP_LABEL[guest.rsvpStatus] || guest.rsvpStatus}
                          color={RSVP_COLOR[guest.rsvpStatus] || 'default'}
                          size="small"
                          variant={guest.rsvpStatus === 'NO_RESPONSE' ? 'outlined' : 'filled'}
                        />
                        {guest.plusOneName && (
                          <Typography variant="caption" display="block" color="text.secondary">
                            +1: {guest.plusOneName}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        {guest.invitationSentAt ? (
                          <Tooltip title={format(new Date(guest.invitationSentAt), 'yyyy. MM. dd. HH:mm', { locale: hu })}>
                            <Chip icon={<CheckIcon />} label="Kiküldve" size="small" color="success" variant="outlined" />
                          </Tooltip>
                        ) : (
                          <Chip label="Nem küldték ki" size="small" variant="outlined" color="default" />
                        )}
                      </TableCell>
                      <TableCell align="right">
                        <Tooltip title={!guest.email ? 'Nincs email cím' : guest.invitationSentAt ? 'Újraküldés' : 'Küldés'}>
                          <span>
                            <IconButton
                              size="small"
                              color="primary"
                              disabled={isDemo || !guest.email || !invitation?.imageUrl}
                              onClick={() => handleSendSingle(guest.id)}
                            >
                              <SendIcon fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>

      <SendInvitationsDialog
        open={sendDialogOpen}
        onClose={() => setSendDialogOpen(false)}
        event={event}
        guests={guests}
        onSuccess={async () => {
          setSendDialogOpen(false);
          await fetchData();
          showSnack('Meghívók kiküldve!');
        }}
      />

      <InvitationPreviewDialog
        open={previewDialogOpen}
        onClose={() => setPreviewDialogOpen(false)}
        invitation={invitation}
        event={event}
        onSaved={(updated) => {
          setInvitation(updated);
          showSnack('Megjelenés mentve!');
        }}
      />

      <Snackbar
        open={snack.open}
        autoHideDuration={4000}
        onClose={() => setSnack(s => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={snack.severity} onClose={() => setSnack(s => ({ ...s, open: false }))}>
          {snack.msg}
        </Alert>
      </Snackbar>
    </Box>
  );
}
